import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { auth, googleProvider, microsoftProvider } from './sso/fire';
import { signInWithPopup } from 'firebase/auth';
import { ASSISTANT_URL, ASSISTANT_AUTH_REGISTER_URL } from '../constants';

/**
 * Register component allows users to create a new account using email/password or social sign-ins (Google/Microsoft).
 * 
 * @returns {JSX.Element} - The rendered Register component.
 */
const Register = () => {
    const [formData, setFormData] = useState({ username: '', email: '', password: '' });
    const [error, setError] = useState(null);
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    /**
     * Handles input changes for the registration form.
     * 
     * @param {Object} e - The event object.
     */
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    /**
     * Handles form submission for email/password registration.
     * 
     * @param {Object} e - The event object.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validate input fields
        if (!formData.username || !formData.email || !formData.password) {
            setError('All fields are required');
            return;
        }

        try {
            const res = await axios.post(ASSISTANT_AUTH_REGISTER_URL, formData, { withCredentials: true });

            if (res.data.token) {
                login(res.data.token); // Store token in context
                navigate('/dashboard'); // Redirect to dashboard
            } else {
                setError('Registration failed. Please try again.');
            }
        } catch (err) {
            console.error('Registration error:', err);
            setError(err.response?.data?.msg || 'An error occurred. Please try again.');
        }
    };

    /**
     * Handles social sign-in (Google/Microsoft) using Firebase authentication.
     * 
     * @param {Object} provider - The Firebase authentication provider (Google or Microsoft).
     */
    const handleSocialSignIn = async (provider) => {
        try {
            const result = await signInWithPopup(auth, provider);
            const token = await result.user.getIdToken();

            const res = await axios.post(
                `${ASSISTANT_URL}/api/auth/${provider === googleProvider ? 'google' : 'microsoft'}`, 
                { token },
                { withCredentials: true }
            );

            if (res.data.token) {
                login(res.data.token); // Store token in context
                navigate('/dashboard'); // Redirect to dashboard
            } else {
                setError(`${provider === googleProvider ? 'Google' : 'Microsoft'} sign-in failed. Please try again.`);
            }
        } catch (error) {
            console.error(`${provider === googleProvider ? 'Google' : 'Microsoft'} sign-in error:`, error);
            setError(`${provider === googleProvider ? 'Google' : 'Microsoft'} sign-in failed. Please try again.`);
        }
    };

    return (
        <div className="flex justify-center items-center h-screen bg-gray-100">
            <div className="w-full max-w-md p-8 bg-white shadow-md rounded-lg">
                <h2 className="text-2xl font-bold mb-6 text-center text-gray-900">Register</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Username</label>
                        <input
                            type="text"
                            name="username"
                            placeholder="Username"
                            value={formData.username}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Password</label>
                        <input
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button
                        type="submit"
                        className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Register
                    </button>
                </form>

                <div className="mt-4">
                    <button
                        onClick={() => handleSocialSignIn(googleProvider)}
                        className="w-full bg-red-500 text-white py-2 px-4 rounded-md shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                        Sign in with Google
                    </button>
                </div>

                <div className="mt-4">
                    <button
                        onClick={() => handleSocialSignIn(microsoftProvider)}
                        className="w-full bg-blue-500 text-white py-2 px-4 rounded-md shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Sign in with Microsoft
                    </button>
                </div>

                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">Already have an account?
                        <Link to="/login" className="text-indigo-600 hover:underline"> Login</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Register;
